import { useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { UpdateCustomerWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseLegend } from "../../../components/form/legend/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import Cookies from "universal-cookie";
import { retrieveStatesService } from "../../../utils/outbound/state/states";
import { retrieveLGAsService } from "../../../utils/outbound/lga/lgas";
import { retrieveStateByIdService } from "../../../utils/outbound/state/stateById";
import { retrieveAllBranchesService } from "../../../utils/api/configuration/branch/retrieveAllBranches";
import { BaseButton } from "../../../components/button/styled";
import { UpdateCustomerFormType } from "../../../types/container.type";
import { retrieveAllSavingsProductsService } from "../../../utils/api/configuration/savingsproduct/retrieveAllSavingsProducts";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { retrieveSavingsAccountByCustomerIdService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { retrieveAllMFIUsersService } from "../../../utils/api/usermanagement/mfiuser/retrieveAllMFIUsers";
import uploadImageIcon from "../../../assets/icons/uploadimageicon.svg";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCountryByIdService } from "../../../utils/outbound/country/countryById";
import { retrieveOtherStatesService } from "../../../utils/outbound/other/states";
import { retrieveOtherCitiesService } from "../../../utils/outbound/other/cities";
import { retrieveAllAccountTiersService } from "../../../utils/api/configuration/tiermanagement/retrieveAllAccountTiers";

export const UpdateCustomer: React.FC<UpdateCustomerFormType> = ({
	formDetails,
	setFormDetails,
	error,
	isLoading,
	handleSubmit,
	customerId,
}) => {
	const genders = ["male", "female"];
	const statuses = ["verified", "unverified", "suspended"];
	const customerLevels: string[] = ["Regular", "HNI", "PEI"];
	const relationships: string[] = [
		"father",
		"mother",
		"brother",
		"sister",
		"son",
		"daughter",
		"other",
	];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const [imagePreview, setImagePreview] = useState<{
		validId: string | null;
		passport: string | null;
		signature: string | null;
	}>({
		validId: null,
		passport: null,
		signature: null,
	});

	useEffect(() => {
		const fetchCustomer = async () => {
			if (!customerId) return;
			try {
				const customer = await retrieveCustomerService(TOKEN, customerId);
				const customerSavingsAccount =
					await retrieveSavingsAccountByCustomerIdService(TOKEN, customerId);
				setFormDetails((prev) => ({
					...prev,
					firstName: customer.firstName || prev.firstName,
					middleName: customer.middleName || prev.middleName,
					lastName: customer.lastName || prev.lastName,
					BVN: customer.BVN || prev.BVN,
					email: customer.email || prev.email,
					gender: customer.gender || prev.gender,
					dob: customer.dob || prev.dob,
					occupation: customer.occupation || prev.occupation,
					address: customer.address || prev.address,
					country: customer.country || prev.country,
					state: customer.state || prev.state,
					lga: customer.lga || prev.lga,
					phone: customer.phone || prev.phone,
					status: customer.status || prev.status,
					validId: customer.validId || prev.validId,
					customerLevel: customer.customerLevel || prev.customerLevel,
					passport: customer.passport || prev.passport,
					signature: customer.signature || prev.signature,
					branch: customerSavingsAccount.branchId || prev.branch,
					accountManager:
						customerSavingsAccount.accountManagerId || prev.accountManager,
					activateEmail:
						customerSavingsAccount.activateEmail || prev.activateEmail,
					activateSMS: customerSavingsAccount.activateSMS || prev.activateSMS,
					autoChargeSMS:
						customerSavingsAccount.autoChargeSMS || prev.autoChargeSMS,
					allowWithdrawal:
						customerSavingsAccount.allowWithdrawal || prev.allowWithdrawal,
					savingsProduct:
						customerSavingsAccount.productId || prev.savingsProduct,
					tier: customerSavingsAccount.tier || prev.tier,
					group: customerSavingsAccount.group || prev.group,
					nextOfKinFirstName:
						customerSavingsAccount.nextOfKinFirstName ||
						prev.nextOfKinFirstName,
					nextOfKinMiddleName:
						customerSavingsAccount.nextOfKinMiddleName ||
						prev.nextOfKinMiddleName,
					nextOfKinLastName:
						customerSavingsAccount.nextOfKinLastName || prev.nextOfKinLastName,
					nextOfKinRelationship:
						customerSavingsAccount.nextOfKinRelationship ||
						prev.nextOfKinRelationship,
					nextOfKinPhone:
						customerSavingsAccount.nextOfKinPhone || prev.nextOfKinPhone,
					nextOfKinEmail:
						customerSavingsAccount.nextOfKinEmail || prev.nextOfKinEmail,
					nextOfKinGender:
						customerSavingsAccount.nextOfKinGender || prev.nextOfKinGender,
					nextOfKinOccupation:
						customerSavingsAccount.nextOfKinOccupation ||
						prev.nextOfKinOccupation,
					nextOfKinResidentialAddress:
						customerSavingsAccount.nextOfKinResidentialAddress ||
						prev.nextOfKinResidentialAddress,
				}));
			} catch (err) {
				console.error("Failed to fetch customer information:", err);
			}
		};
		fetchCustomer();
	}, [TOKEN, customerId, setFormDetails]);

	const { data: states } = useQuery({
		queryKey: ["states", TOKEN, formDetails.country],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const country = formDetails?.country?.trim() || "";
			if (!country) return;
			let response;
			if (country === "Nigeria") {
				response = await retrieveStatesService(user?._id);
			} else {
				const institution = await retrieveInstitutionByIdService(
					user?._id,
					user?.institution,
				);
				const countryInfo = await retrieveCountryByIdService(
					user?._id,
					institution.country,
				);
				const res = await retrieveOtherStatesService({
					country: countryInfo?.title,
				});
				response = res?.data?.states;
			}
			return response;
		},
		enabled: !!TOKEN && !!formDetails.country?.trim(),
	});

	const { data: lgas } = useQuery({
		queryKey: ["LGAs", TOKEN, formDetails.country, formDetails.state],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const country = formDetails?.country?.trim() || "";
			if (!country) return;
			const stateId = formDetails.state?.trim() ? formDetails.state : null;
			if (!stateId) return;
			let response;
			if (country === "Nigeria") {
				const state = await retrieveStateByIdService(user?._id, stateId);
				response = await retrieveLGAsService(state.title);
			} else {
				const institution = await retrieveInstitutionByIdService(
					user?._id,
					user?.institution,
				);
				const countryInfo = await retrieveCountryByIdService(
					user?._id,
					institution.country,
				);
				const res = await retrieveOtherStatesService({
					country: countryInfo?.title,
				});
				const state = res?.data?.states?.[Number(stateId) - 1];
				const cities = await retrieveOtherCitiesService({
					country: countryInfo?.title,
					state: state?.name,
				});
				response = cities?.data;
			}
			return response;
		},
		enabled:
			!!TOKEN && !!formDetails.country?.trim() && !!formDetails.state?.trim(),
	});

	const { data: branches } = useQuery({
		queryKey: ["branchesForUpdateCustomer", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllBranchesService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: savingsProducts } = useQuery({
		queryKey: ["savingsProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllSavingsProductsService(TOKEN);
			const user = await retrieveUserProfileService(TOKEN);
			return response.filter(
				(product: Record<string, any>) =>
					// product.serialNo === 1 &&
					product.status === "active" &&
					product.institution === user.institution,
			);
		},
		enabled: !!TOKEN,
	});

	const { data: tiers } = useQuery({
		queryKey: ["tiers", TOKEN],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const institution = await retrieveInstitutionByIdService(
				user?._id,
				user?.institution,
			);
			const country = await retrieveCountryByIdService(
				user?._id,
				institution.country,
			);
			const response = await retrieveAllAccountTiersService(TOKEN);
			const transformedResponse = response?.data?.filter(
				(tier: Record<string, any>) => tier.country === country?.id,
			);
			return transformedResponse;
		},
		enabled: !!TOKEN,
	});

	const { data: accountManagers } = useQuery({
		queryKey: ["accountManagers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllMFIUsersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const target = e.target as HTMLInputElement;
		const { name, files } = target;
		if (files && files[0]) {
			const reader = new FileReader();
			reader.onload = (e) => {
				if (e.target?.result) {
					setImagePreview((prev) => ({
						...prev,
						[name]: e.target?.result as string,
					}));
					setFormDetails((prev) => ({
						...prev,
						[name]: files[0],
					}));
				}
			};
			reader.readAsDataURL(files[0]);
		}
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: !prev[name as keyof typeof formDetails],
		}));
	};

	return (
		<UpdateCustomerWrapper onSubmit={handleSubmit}>
			<BaseLegend fontweight={500} fontsize={"18"} color="var(--dark-color)">
				Enter required Information to update a customer account
			</BaseLegend>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							First Name
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="firstName"
							value={formDetails.firstName}
							placeholder="Enter First Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Middle Name</BaseLabel>
						<BaseInput
							name="middleName"
							value={formDetails.middleName}
							placeholder="Enter Middle Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Last Name{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="lastName"
							value={formDetails.lastName}
							placeholder="Enter Last Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							BVN{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="BVN"
							value={formDetails.BVN}
							placeholder="Enter BVN"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Email{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="email"
							type="email"
							value={formDetails.email}
							placeholder="Enter Email Address"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Gender{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="gender"
							fontweight={400}
							value={formDetails.gender}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Gender</BaseOption>
							{genders?.map((gender: string, index: number) => {
								return (
									<BaseOption key={index} value={gender}>
										{gender.charAt(0).toUpperCase() + gender.slice(1)}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Date of Birth{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="dob"
							type="date"
							value={formDetails.dob}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Occupation{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="occupation"
							value={formDetails.occupation}
							placeholder="Enter Occupation"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Residential Address
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							multiline
							minRows={4}
							name="address"
							value={formDetails.address}
							placeholder="Enter Residential Address"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Country{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							readOnly
							name="country"
							value={formDetails.country}
							placeholder="Country"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							State/Province{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="state"
							fontweight={400}
							value={formDetails.state}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>
								Select State/Province{" "}
								<Typography
									component={"span"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"var(--text-salmon-pink-color)"}
								>
									*
								</Typography>
							</BaseOption>
							{states?.map((state: Record<string, any>, index: number) => {
								return (
									<BaseOption key={index} value={String(state.id || index + 1)}>
										{state?.title || state?.name}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							LGA{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="lga"
							fontweight={400}
							value={formDetails.lga}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select LGA/City</BaseOption>
							{lgas?.map((lga: Record<string, any>, index: number) => {
								return (
									<BaseOption key={index} value={String(lga.id || index + 1)}>
										{lga.title || lga}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Phone{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="phone"
							value={formDetails.phone}
							placeholder="Enter Phone Number"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Branch
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							readOnly
							name="branch"
							fontweight={400}
							value={formDetails.branch}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Branch</BaseOption>
							{branches?.map((branch: Record<string, any>, index: number) => {
								return (
									<BaseOption key={index} value={branch.id}>
										{branch.name}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Account Officer{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="accountManager"
							fontweight={400}
							value={formDetails.accountManager}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Account Manager</BaseOption>
							{accountManagers?.map(
								(accountManager: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={accountManager.id}>
											{`${accountManager?.firstName} ${accountManager?.lastName}`}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Status{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							name="status"
							fontweight={400}
							value={formDetails.status}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Status</BaseOption>
							{statuses?.map((status: string, index: number) => {
								return (
									<BaseOption key={index} value={status}>
										{status.charAt(0).toUpperCase() +
											status.slice(1).toLowerCase()}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Valid ID{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<Stack
							direction={"row"}
							alignItems={"center"}
							gap={{ mobile: "calc(var(--flex-gap)/4)" }}
						>
							{imagePreview?.validId ? (
								<img
									src={imagePreview?.validId}
									alt="file-badge"
									className="file-badge"
								/>
							) : (
								<img
									src={(formDetails?.validId as string) || uploadImageIcon}
									alt="file-badge"
									className="file-badge"
								/>
							)}
							<BaseInput
								as={"input"}
								type="file"
								name="validId"
								sx={{ width: "100%" }}
								inputProps={{ accept: "image/jpeg, image/png" }}
								onChange={(e) => handleFileChange(e)}
							/>
						</Stack>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Customer Level{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="customerLevel"
							fontweight={400}
							value={formDetails.customerLevel}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Customer Level</BaseOption>
							{customerLevels?.map((customerLevel, index: number) => {
								return (
									<BaseOption key={index} value={customerLevel}>
										{customerLevel}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 3 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Activate Email{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseLabel
							sx={{
								display: "flex",
								gap: "calc(var(--flex-gap)/4)",
								alignItems: "center",
								justifyContent: "space-between",
								marginBlock: "0",
								padding: "calc(var(--basic-padding)/2)",
								border: "1px solid var(--border-color)",
								borderRadius: "8px",
								overflow: "hidden",
							}}
						>
							<Typography
								component={"span"}
								variant="subtitle1"
								fontFamily={"Inter"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color={"var(--dark-color)"}
							>
								Check Option
							</Typography>
							<BaseInput
								type="checkbox"
								name="activateEmail"
								inputProps={{
									checked: formDetails.activateEmail,
								}}
								onChange={handleCheckboxChange}
								sx={{
									border: "none",
									padding: 0,
								}}
							/>
						</BaseLabel>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 3 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Activate SMS{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseLabel
							sx={{
								display: "flex",
								gap: "calc(var(--flex-gap)/4)",
								alignItems: "center",
								justifyContent: "space-between",
								marginBlock: "0",
								padding: "calc(var(--basic-padding)/2)",
								border: "1px solid var(--border-color)",
								borderRadius: "8px",
								overflow: "hidden",
							}}
						>
							<Typography
								component={"span"}
								variant="subtitle1"
								fontFamily={"Inter"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color={"var(--dark-color)"}
							>
								Check Option
							</Typography>
							<BaseInput
								type="checkbox"
								name="activateSMS"
								inputProps={{
									checked: formDetails.activateSMS,
								}}
								onChange={handleCheckboxChange}
								sx={{
									border: "none",
									padding: 0,
								}}
							/>
						</BaseLabel>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 3 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Auto Charge SMS{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseLabel
							sx={{
								display: "flex",
								gap: "calc(var(--flex-gap)/4)",
								alignItems: "center",
								justifyContent: "space-between",
								marginBlock: "0",
								padding: "calc(var(--basic-padding)/2)",
								border: "1px solid var(--border-color)",
								borderRadius: "8px",
								overflow: "hidden",
							}}
						>
							<Typography
								component={"span"}
								variant="subtitle1"
								fontFamily={"Inter"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color={"var(--dark-color)"}
							>
								Check Option
							</Typography>
							<BaseInput
								type="checkbox"
								name="autoChargeSMS"
								inputProps={{
									checked: formDetails.autoChargeSMS,
								}}
								onChange={handleCheckboxChange}
								sx={{
									border: "none",
									padding: 0,
								}}
							/>
						</BaseLabel>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 3 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Allow Withdrawal{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseLabel
							sx={{
								display: "flex",
								gap: "calc(var(--flex-gap)/4)",
								alignItems: "center",
								justifyContent: "space-between",
								marginBlock: "0",
								padding: "calc(var(--basic-padding)/2)",
								border: "1px solid var(--border-color)",
								borderRadius: "8px",
								overflow: "hidden",
							}}
						>
							<Typography
								component={"span"}
								variant="subtitle1"
								fontFamily={"Inter"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color={"var(--dark-color)"}
							>
								Check Option
							</Typography>
							<BaseInput
								type="checkbox"
								name="allowWithdrawal"
								inputProps={{
									checked: formDetails.allowWithdrawal,
								}}
								onChange={handleCheckboxChange}
								sx={{
									border: "none",
									padding: 0,
								}}
							/>
						</BaseLabel>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Customer Passport{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<Stack
							direction={"row"}
							alignItems={"center"}
							gap={{ mobile: "calc(var(--flex-gap)/4)" }}
						>
							{imagePreview?.passport ? (
								<img
									src={imagePreview?.passport}
									alt="file-badge"
									className="file-badge"
								/>
							) : (
								<img
									src={(formDetails?.passport as string) || uploadImageIcon}
									alt="file-badge"
									className="file-badge"
								/>
							)}
							<BaseInput
								as={"input"}
								type="file"
								name="passport"
								sx={{ width: "100%" }}
								inputProps={{ accept: "image/jpeg, image/png" }}
								onChange={(e) => handleFileChange(e)}
							/>
						</Stack>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Customer Signature{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<Stack
							direction={"row"}
							alignItems={"center"}
							gap={{ mobile: "calc(var(--flex-gap)/4)" }}
						>
							{imagePreview?.signature ? (
								<img
									src={imagePreview?.signature}
									alt="file-badge"
									className="file-badge"
								/>
							) : (
								<img
									src={(formDetails?.signature as string) || uploadImageIcon}
									alt="file-badge"
									className="file-badge"
								/>
							)}
							<BaseInput
								as={"input"}
								type="file"
								name="signature"
								sx={{ width: "100%" }}
								inputProps={{ accept: "image/jpeg, image/png" }}
								onChange={(e) => handleFileChange(e)}
							/>
						</Stack>
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			<Box>
				<Typography
					variant="h3"
					fontFamily={"Poppins"}
					fontWeight={700}
					fontSize={20}
					lineHeight={"normal"}
					color="var(--dark-color)"
				>
					Next of Kin Information
				</Typography>
			</Box>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							First Name{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="nextOfKinFirstName"
							value={formDetails.nextOfKinFirstName}
							placeholder="Enter First Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Middle Name</BaseLabel>
						<BaseInput
							name="nextOfKinMiddleName"
							value={formDetails.nextOfKinMiddleName}
							placeholder="Enter Middle Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Last Name{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="nextOfKinLastName"
							value={formDetails.nextOfKinLastName}
							placeholder="Enter Last Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Relationship{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="nextOfKinRelationship"
							fontweight={400}
							value={formDetails.nextOfKinRelationship}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Option</BaseOption>
							{relationships?.map((relationship: string, index: number) => {
								return (
									<BaseOption key={index} value={relationship}>
										{relationship.charAt(0).toUpperCase() +
											relationship.slice(1)}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Phone{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="nextOfKinPhone"
							value={formDetails.nextOfKinPhone}
							placeholder="Enter Phone"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Email{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							type="email"
							name="nextOfKinEmail"
							value={formDetails.nextOfKinEmail}
							placeholder="Enter Email"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Gender{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="nextOfKinGender"
							fontweight={400}
							value={formDetails.nextOfKinGender}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Gender</BaseOption>
							{genders?.map((gender: string, index: number) => {
								return (
									<BaseOption key={index} value={gender}>
										{gender.charAt(0).toUpperCase() + gender.slice(1)}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Occupation{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="nextOfKinOccupation"
							value={formDetails.nextOfKinOccupation}
							placeholder="Enter Occupation"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Residential Address
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="nextOfKinResidentialAddress"
							value={formDetails.nextOfKinResidentialAddress}
							placeholder="Enter Address"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			<Box>
				<Typography
					variant="h3"
					fontFamily={"Poppins"}
					fontWeight={700}
					fontSize={20}
					lineHeight={"normal"}
					color="var(--dark-color)"
				>
					Savings Account Information
				</Typography>
			</Box>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Savings Product{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							readOnly
							name="savingsProduct"
							fontweight={400}
							value={formDetails.savingsProduct}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Savings Product</BaseOption>
							{savingsProducts?.map(
								(savingsProduct: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={savingsProduct.id}>
											{savingsProduct.name}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Account Tier{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="tier"
							fontweight={400}
							value={formDetails.tier}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Tier</BaseOption>
							{tiers?.map((tier: Record<string, any>, index: number) => {
								return (
									<BaseOption key={index} value={tier.id}>
										{tier.name}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
				>
					{error}
				</Typography>
			)}
			<Box overflow={"hidden"}>
				<BaseButton
					type="submit"
					variant="contained"
					bgcolor="var(--primary-color)"
					disableElevation
					sx={{
						width: { mobile: "100%", miniTablet: "auto" },
					}}
				>
					{isLoading ? (
						<CircularProgress color="inherit" className="loader" />
					) : (
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Update Customer
						</Typography>
					)}
				</BaseButton>
			</Box>
		</UpdateCustomerWrapper>
	);
};
